import React from 'react';
import {
  ACTION_LABELS,
  ActionRequest,
  ActionResolution,
  executeActionRequest,
} from '../../../services/trips/actionRequests';
import ActionResolutionView from './ActionResolutionView';
import './chatCards.css';

interface ActionRequestCardProps {
  request: ActionRequest;
  resolution?: ActionResolution;
  createdTripId?: string;
  resolutionNote?: string;
  backTo?: string;
  conversationId?: string;
  // UUID of the trip already created for this chat. When set, a CREATE_TRIP
  // proposal is blocked (a chat gets at most one trip).
  existingTripId?: string;
  fallbackActivities?: Array<Record<string, any>>;
  onResolve: (resolution: ActionResolution, note: string, tripId?: string) => void;
}

const summarize = (request: ActionRequest): string | null => {
  const payload = request.payload || {};
  switch (request.action) {
    case 'ADD_ACTIVITIES': {
      const names = (payload.activities || [])
        .map((activity: any) => activity.name || activity.title)
        .filter(Boolean);
      return names.length ? names.join(' · ') : null;
    }
    case 'UPDATE_ACTIVITY':
      return payload.name || null;
    case 'UPDATE_TRIP':
      return Object.keys(payload).filter((key) => key !== 'activity_id').join(', ') || null;
    default:
      return null;
  }
};

const ActionRequestCard: React.FC<ActionRequestCardProps> = ({
  request,
  resolution,
  createdTripId,
  resolutionNote,
  backTo = '/ask-ai',
  conversationId,
  existingTripId,
  fallbackActivities,
  onResolve,
}) => {
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await executeActionRequest(request, { conversationId, fallbackActivities });
      onResolve('confirmed', result.note, result.tripId);
    } catch (error: any) {
      onResolve('failed', error?.message || 'The change could not be applied');
    } finally {
      setBusy(false);
    }
  };

  if (resolution) {
    return (
      <ActionResolutionView
        resolution={resolution}
        note={resolutionNote}
        tripId={createdTripId}
        backTo={backTo}
      />
    );
  }

  // A chat gets at most one trip: if one already exists for this conversation,
  // block the CREATE_TRIP proposal and point at the existing trip instead.
  if (request.action === 'CREATE_TRIP' && existingTripId) {
    return (
      <ActionResolutionView
        resolution="confirmed"
        note="This chat already has a trip"
        tripId={existingTripId}
        backTo={backTo}
      />
    );
  }

  const summary = summarize(request);

  return (
    <div className="v2-chat-card" role="group" aria-label="Confirm suggested change">
      <p className="v2-chat-card__title">{ACTION_LABELS[request.action] || request.action}?</p>
      {summary && <p className="v2-chat-card__summary">{summary}</p>}
      <div className="v2-chat-card__actions">
        <button type="button" className="v2-chat-card__confirm" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Applying…' : 'Confirm'}
        </button>
        <button
          type="button"
          className="v2-chat-card__cancel"
          onClick={() => onResolve('dismissed', 'Dismissed')}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ActionRequestCard;
